import { Box, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { MassMessageHistory } from '../../Services/API';
import { withClickTracking } from '../Tracked/withMixpanelTracking';
import { TrackableEvents } from '../Tracked/events';
import { AccessTime } from '@mui/icons-material';
import moment from 'moment';
import TrashIcon from '../../Assets/trash.svg';
import EditIcon from '../../Assets/editMessage.svg';

interface MessageCardProps {
  message: MassMessageHistory;
  isLoading?: boolean;
  onCopyMessage?: (message: MassMessageHistory) => void;
  onDelete?: (message: MassMessageHistory) => void;
}

const TrackedLabel = withClickTracking(Typography)(TrackableEvents.ButtonClicked, {
  button_location: 'Mass Notify',
  button_feature: 'Mass Notify',
  button_type: 'link',
});

export const MessageCard = (props: MessageCardProps) => {
  const { message, isLoading, onCopyMessage, onDelete } = props;
  const theme = useTheme();

  const localCreatedDate = moment.utc(message.created).local();

  return (
    <Stack
      sx={{
        border: 1,
        borderRadius: 2,
        padding: 2,
        borderColor: theme.palette.border.input,
        textAlign: 'left',
        height: '120px',
        justifyContent: 'space-between',
      }}>
      <Stack direction='row' justifyContent='space-between' gap={2}>
        <Typography
          variant='subtitle1'
          maxHeight='inherit'
          overflow={'hidden'}
          whiteSpace={'nowrap'}
          textOverflow={'ellipsis'}>
          {message.headline ? message.headline : localCreatedDate.format('[Sent] MMM D, YYYY [at] h:mm A')}
        </Typography>
        {!isLoading && (
          <Stack direction='row' gap={1}>
            {message.isTemplate && (
              <TrackedLabel
                onClick={() => onDelete?.(message)}
                mpExtraData={{
                  button_label: 'Delete Template',
                  message_id: message.id,
                  message: message.message,
                  recipients: message.deliveredRecipients,
                  channel: message.channel,
                }}
                sx={{
                  color: theme.palette.primary.main,
                  '&:hover': { color: theme.palette.primary.light, cursor: 'pointer' },
                }}>
                <img src={TrashIcon} alt='Delete Template' style={{ width: '20px', height: '20px' }} />
              </TrackedLabel>
            )}
            <TrackedLabel
              onClick={() => onCopyMessage?.(message)}
              mpExtraData={{
                button_label: 'Copy Message',
                message_id: message.id,
                message: message.message,
                recipients: message.deliveredRecipients,
                channel: message.channel,
              }}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': { color: theme.palette.primary.light, cursor: 'pointer' },
              }}>
              <img src={EditIcon} alt='Populate Message' style={{ width: '20px', height: '20px' }} />
            </TrackedLabel>
          </Stack>
        )}
      </Stack>

      <Typography
        variant='body2'
        maxHeight='30px'
        sx={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
        {message.message}
      </Typography>

      <Stack direction='row' justifyContent={message.isTemplate !== true ? 'space-between' : 'end'}>
        {message.isTemplate !== true && (
          <Stack direction='row' alignItems='center' gap={1}>
            <AccessTime sx={{ fontSize: 12, color: theme.palette.text.secondary }} />
            <Typography variant='overline' color='textSecondary'>
              {localCreatedDate.format('MM/DD/YYYY, hh:mm A')}
            </Typography>
          </Stack>
        )}

        {!message.isTemplate && (
          <Tooltip
            disableHoverListener={message.deliveredRecipients.length < 1}
            disableTouchListener={message.deliveredRecipients.length < 1}
            title={
              <Box style={{ maxHeight: '400px', padding: '0px 8px', overflowY: 'scroll' }}>
                <ul>
                  {message.deliveredRecipients.map(dr => (
                    <li key={dr.recipientId}>
                      <Typography variant='caption'>{dr.recipientName}</Typography>
                    </li>
                  ))}
                </ul>
              </Box>
            }>
            <Typography
              variant='caption'
              color='textSecondary'
              sx={{
                '&:hover': { color: theme.palette.text.primary },
              }}>
              {`${message.deliveredRecipients.length} ${
                message.deliveredRecipients.length === 1 ? 'Recipient' : 'Recipients'
              }`}
            </Typography>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
};

export default MessageCard;
